// @ts-nocheck
/**
 * Learning Dashboard Page - Carbon Design System Compliant
 * Comprehensive learning analytics and progress tracking for individual professionals
 * WCAG 2.1 AA accessibility compliant
 */

import React, { useState, useEffect } from 'react';
import {
  Content,
  Grid,
  Column,
  Heading,
  Stack,
  Button,
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  ProgressBar,
  Card,
  Tile,
  Modal,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  InlineNotification,
  Loading,
  Breadcrumb,
  BreadcrumbItem,
  DatePicker,
  DatePickerInput,
  Select,
  SelectItem,
  OverflowMenu,
  OverflowMenuItem,
} from '@carbon/react';
import {
  Analytics,
  Book,
  Trophy,
  Growth,
  Calendar,
  Dashboard,
  ChartLine,
  Time,
  Checkmark,
  View,
  Download,
  Filter,
  ArrowRight,
  PlayFilled,
} from '@carbon/icons-react';

interface LearningPath {
  id: string;
  title: string;
  description: string;
  category: string;
  progress: number;
  totalModules: number;
  completedModules: number;
  estimatedTime: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  lastAccessed: string;
  status: 'Not Started' | 'In Progress' | 'Completed' | 'Paused';
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  dateEarned: string;
  category: string;
  points: number;
  badgeIcon: string;
}

interface LearningActivity {
  id: string;
  title: string;
  type: 'Course' | 'Assessment' | 'Project' | 'Reading';
  duration: string;
  completedAt: string;
  score?: number;
  status: 'Completed' | 'In Progress' | 'Scheduled';
}

const LearningDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [selectedDateRange, setSelectedDateRange] = useState('30days');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [error, setError] = useState<string | null>(null);

  // Sample data - in real app, this would come from API
  const [learningStats, setLearningStats] = useState({
    totalHours: 47.5,
    coursesCompleted: 8,
    certificationsEarned: 3,
    currentStreak: 12,
    weeklyGoal: 10,
    weeklyProgress: 7.5,
    skillsImproved: 15,
    averageScore: 87,
  });

  const [learningPaths, setLearningPaths] = useState<LearningPath[]>([
    {
      id: '1',
      title: 'Full Stack Development Mastery',
      description: 'Complete web development from frontend to backend',
      category: 'Web Development',
      progress: 65,
      totalModules: 24,
      completedModules: 16,
      estimatedTime: '120 hours',
      difficulty: 'Advanced',
      lastAccessed: '2024-01-15',
      status: 'In Progress',
    },
    {
      id: '2',
      title: 'Cloud Architecture Fundamentals',
      description: 'Learn cloud computing principles and AWS services',
      category: 'Cloud Computing',
      progress: 30,
      totalModules: 18,
      completedModules: 5,
      estimatedTime: '80 hours',
      difficulty: 'Intermediate',
      lastAccessed: '2024-01-12',
      status: 'In Progress',
    },
    {
      id: '3',
      title: 'Machine Learning Basics',
      description: 'Introduction to ML algorithms and data science',
      category: 'Data Science',
      progress: 0,
      totalModules: 20,
      completedModules: 0,
      estimatedTime: '100 hours',
      difficulty: 'Beginner',
      lastAccessed: '',
      status: 'Not Started',
    },
  ]);

  const [achievements, setAchievements] = useState<Achievement[]>([
    {
      id: '1',
      title: 'JavaScript Master',
      description: 'Completed advanced JavaScript course with 95% score',
      dateEarned: '2024-01-10',
      category: 'Programming',
      points: 500,
      badgeIcon: 'trophy',
    },
    {
      id: '2',
      title: 'Week Warrior',
      description: 'Maintained 7-day learning streak',
      dateEarned: '2024-01-08',
      category: 'Consistency',
      points: 200,
      badgeIcon: 'streak',
    },
    {
      id: '3',
      title: 'Quick Learner',
      description: 'Completed course 20% faster than average',
      dateEarned: '2024-01-05',
      category: 'Efficiency',
      points: 300,
      badgeIcon: 'speed',
    },
  ]);

  const [recentActivities, setRecentActivities] = useState<LearningActivity[]>([
    {
      id: '1',
      title: 'React Hooks Deep Dive',
      type: 'Course',
      duration: '2.5 hours',
      completedAt: '2024-01-15T14:30:00Z',
      score: 92,
      status: 'Completed',
    },
    {
      id: '2',
      title: 'Node.js Assessment',
      type: 'Assessment',
      duration: '45 minutes',
      completedAt: '2024-01-14T16:00:00Z',
      score: 88,
      status: 'Completed',
    },
    {
      id: '3',
      title: 'Portfolio Project Review',
      type: 'Project',
      duration: '3 hours',
      completedAt: '2024-01-13T18:00:00Z',
      status: 'Completed',
    },
    {
      id: '4',
      title: 'AWS Fundamentals',
      type: 'Course',
      duration: '1.5 hours',
      completedAt: '',
      status: 'In Progress',
    },
  ]);

  useEffect(() => {
    // Simulate API loading
    setTimeout(() => setLoading(false), 1000);
  }, []);

  const pathTableHeaders = [
    { key: 'title', header: 'Learning Path' },
    { key: 'category', header: 'Category' },
    { key: 'progress', header: 'Progress' },
    { key: 'difficulty', header: 'Difficulty' },
    { key: 'lastAccessed', header: 'Last Accessed' },
    { key: 'actions', header: 'Actions' },
  ];

  const pathTableRows = learningPaths.map(path => ({
    id: path.id,
    title: (
      <div>
        <p style={{ margin: 0, fontWeight: 500 }}>{path.title}</p>
        <p style={{ margin: 0, fontSize: 'var(--cds-type-scale-01)', color: 'var(--cds-text-secondary)' }}>
          {path.completedModules}/{path.totalModules} modules • {path.estimatedTime}
        </p>
      </div>
    ),
    category: (
      <Tag type="blue" size="sm">
        {path.category}
      </Tag>
    ),
    progress: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-03)' }}>
        <ProgressBar
          value={path.progress}
          max={100}
          label={`${path.progress}%`}
          hideLabel={false}
          size="sm"
          style={{ flex: 1, minWidth: '120px' }}
        />
      </div>
    ),
    difficulty: (
      <Tag
        type={path.difficulty === 'Advanced' ? 'red' : path.difficulty === 'Intermediate' ? 'yellow' : 'green'}
        size="sm"
      >
        {path.difficulty}
      </Tag>
    ),
    lastAccessed: path.lastAccessed || 'Never',
    actions: (
      <div style={{ display: 'flex', gap: 'var(--cds-spacing-02)' }}>
        <Button kind="primary" size="sm" renderIcon={path.status === 'Not Started' ? PlayFilled : ArrowRight}>
          {path.status === 'Not Started' ? 'Start' : 'Continue'}
        </Button>
        <OverflowMenu>
          <OverflowMenuItem itemText="View Details" />
          <OverflowMenuItem itemText="Download Certificate" />
          <OverflowMenuItem itemText="Share Progress" />
        </OverflowMenu>
      </div>
    ),
  }));

  const activityTableHeaders = [
    { key: 'activity', header: 'Activity' },
    { key: 'type', header: 'Type' },
    { key: 'duration', header: 'Duration' },
    { key: 'score', header: 'Score' },
    { key: 'status', header: 'Status' },
  ];

  const activityTableRows = recentActivities.map(activity => ({
    id: activity.id,
    activity: activity.title,
    type: (
      <Tag
        type={
          activity.type === 'Course'
            ? 'blue'
            : activity.type === 'Assessment'
              ? 'purple'
              : activity.type === 'Project'
                ? 'green'
                : 'gray'
        }
        size="sm"
      >
        {activity.type}
      </Tag>
    ),
    duration: activity.duration,
    score: activity.score ? `${activity.score}%` : '—',
    status: (
      <Tag type={activity.status === 'Completed' ? 'green' : 'blue'} size="sm">
        {activity.status}
      </Tag>
    ),
  }));

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Advanced':
        return 'red';
      case 'Intermediate':
        return 'yellow';
      default:
        return 'green';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'green';
      case 'In Progress':
        return 'blue';
      case 'Paused':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (
      <Content>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <Loading description="Loading learning dashboard..." />
        </div>
      </Content>
    );
  }

  return (
    <Content>
      <Grid>
        {/* Breadcrumb Navigation */}
        <Column lg={16} md={8} sm={4}>
          <Breadcrumb>
            <BreadcrumbItem href="/dashboard">Dashboard</BreadcrumbItem>
            <BreadcrumbItem href="/dashboard/learning" isCurrentPage>
              Learning Dashboard
            </BreadcrumbItem>
          </Breadcrumb>
        </Column>

        {/* Page Header */}
        <Column lg={16} md={8} sm={4} style={{ marginTop: 'var(--cds-spacing-06)' }}>
          <Stack gap={3}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Heading size="lg">Learning Dashboard</Heading>
              <div style={{ display: 'flex', gap: 'var(--cds-spacing-03)' }}>
                <Select
                  id="dateRange"
                  labelText=""
                  value={selectedDateRange}
                  onChange={e => setSelectedDateRange(e.target.value)}
                  size="sm"
                >
                  <SelectItem value="7days" text="Last 7 days" />
                  <SelectItem value="30days" text="Last 30 days" />
                  <SelectItem value="90days" text="Last 90 days" />
                  <SelectItem value="1year" text="Last year" />
                </Select>
                <Button kind="tertiary" size="sm" renderIcon={Download}>
                  Export Report
                </Button>
              </div>
            </div>
            <p style={{ color: 'var(--cds-text-secondary)' }}>
              Track your learning progress, view achievements, and manage your professional development journey.
            </p>
          </Stack>
        </Column>

        {/* Error Notification */}
        {error && (
          <Column lg={16} md={8} sm={4}>
            <InlineNotification kind="error" title="Error" subtitle={error} onClose={() => setError(null)} />
          </Column>
        )}

        {/* Learning Stats Overview */}
        <Column lg={16} md={8} sm={4} style={{ marginTop: 'var(--cds-spacing-06)' }}>
          <Grid condensed>
            <Column sm={2} md={2} lg={4}>
              <Tile style={{ padding: 'var(--cds-spacing-05)', textAlign: 'center' }}>
                <Time size={24} style={{ color: 'var(--cds-interactive-01)', marginBottom: 'var(--cds-spacing-03)' }} />
                <Heading size="sm">{learningStats.totalHours}h</Heading>
                <p style={{ color: 'var(--cds-text-secondary)', margin: 0, fontSize: 'var(--cds-type-scale-01)' }}>
                  Total Learning Time
                </p>
              </Tile>
            </Column>
            <Column sm={2} md={2} lg={4}>
              <Tile style={{ padding: 'var(--cds-spacing-05)', textAlign: 'center' }}>
                <Book
                  size={24}
                  style={{ color: 'var(--cds-support-success)', marginBottom: 'var(--cds-spacing-03)' }}
                />
                <Heading size="sm">{learningStats.coursesCompleted}</Heading>
                <p style={{ color: 'var(--cds-text-secondary)', margin: 0, fontSize: 'var(--cds-type-scale-01)' }}>
                  Courses Completed
                </p>
              </Tile>
            </Column>
            <Column sm={2} md={2} lg={4}>
              <Tile style={{ padding: 'var(--cds-spacing-05)', textAlign: 'center' }}>
                <Trophy
                  size={24}
                  style={{ color: 'var(--cds-support-warning)', marginBottom: 'var(--cds-spacing-03)' }}
                />
                <Heading size="sm">{learningStats.certificationsEarned}</Heading>
                <p style={{ color: 'var(--cds-text-secondary)', margin: 0, fontSize: 'var(--cds-type-scale-01)' }}>
                  Certifications
                </p>
              </Tile>
            </Column>
            <Column sm={2} md={2} lg={4}>
              <Tile style={{ padding: 'var(--cds-spacing-05)', textAlign: 'center' }}>
                <ChartLine
                  size={24}
                  style={{ color: 'var(--cds-support-info)', marginBottom: 'var(--cds-spacing-03)' }}
                />
                <Heading size="sm">{learningStats.averageScore}%</Heading>
                <p style={{ color: 'var(--cds-text-secondary)', margin: 0, fontSize: 'var(--cds-type-scale-01)' }}>
                  Average Score
                </p>
              </Tile>
            </Column>
          </Grid>
        </Column>

        {/* Weekly Goal Progress */}
        <Column lg={8} md={4} sm={4} style={{ marginTop: 'var(--cds-spacing-06)' }}>
          <Tile style={{ padding: 'var(--cds-spacing-06)' }}>
            <Stack gap={4}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Heading size="md">Weekly Learning Goal</Heading>
                <Tag type="blue" size="sm">
                  {learningStats.currentStreak} day streak
                </Tag>
              </div>

              <div>
                <p
                  style={{
                    margin: '0 0 var(--cds-spacing-03) 0',
                    color: 'var(--cds-text-secondary)',
                    fontSize: 'var(--cds-type-scale-02)',
                  }}
                >
                  {learningStats.weeklyProgress} of {learningStats.weeklyGoal} hours this week
                </p>
                <ProgressBar
                  value={learningStats.weeklyProgress}
                  max={learningStats.weeklyGoal}
                  label={`${Math.round((learningStats.weeklyProgress / learningStats.weeklyGoal) * 100)}%`}
                  hideLabel={false}
                  size="lg"
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: 'var(--cds-type-scale-01)',
                    color: 'var(--cds-text-secondary)',
                  }}
                >
                  {learningStats.weeklyGoal - learningStats.weeklyProgress} hours remaining
                </p>
                <Button kind="ghost" size="sm">
                  Adjust Goal
                </Button>
              </div>
            </Stack>
          </Tile>
        </Column>

        {/* Recent Achievements */}
        <Column lg={8} md={4} sm={4} style={{ marginTop: 'var(--cds-spacing-06)' }}>
          <Tile style={{ padding: 'var(--cds-spacing-06)' }}>
            <Stack gap={4}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Heading size="md">Recent Achievements</Heading>
                <Button kind="ghost" size="sm" renderIcon={View}>
                  View All
                </Button>
              </div>

              <Stack gap={3}>
                {achievements.slice(0, 3).map(achievement => (
                  <div
                    key={achievement.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--cds-spacing-04)',
                      padding: 'var(--cds-spacing-04)',
                      border: '1px solid var(--cds-border-subtle-01)',
                      borderRadius: 'var(--cds-border-radius)',
                      backgroundColor: 'var(--cds-layer-02)',
                    }}
                  >
                    <Trophy size={24} style={{ color: 'var(--cds-support-warning)' }} />
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: 500, fontSize: 'var(--cds-type-scale-02)' }}>
                        {achievement.title}
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 'var(--cds-type-scale-01)',
                          color: 'var(--cds-text-secondary)',
                        }}
                      >
                        {achievement.description}
                      </p>
                    </div>
                    <Tag type="blue" size="sm">
                      {achievement.points} pts
                    </Tag>
                  </div>
                ))}
              </Stack>
            </Stack>
          </Tile>
        </Column>

        {/* Main Content Tabs */}
        <Column lg={16} md={8} sm={4} style={{ marginTop: 'var(--cds-spacing-07)' }}>
          <Tabs>
            <TabList aria-label="Learning dashboard tabs">
              <Tab>Learning Paths</Tab>
              <Tab>Recent Activity</Tab>
              <Tab>Analytics</Tab>
            </TabList>
            <TabPanels>
              {/* Learning Paths Tab */}
              <TabPanel>
                <Stack gap={5} style={{ marginTop: 'var(--cds-spacing-05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Heading size="md">Your Learning Paths</Heading>
                    <div style={{ display: 'flex', gap: 'var(--cds-spacing-03)' }}>
                      <Select
                        id="categoryFilter"
                        labelText=""
                        value={selectedCategory}
                        onChange={e => setSelectedCategory(e.target.value)}
                        size="sm"
                      >
                        <SelectItem value="all" text="All Categories" />
                        <SelectItem value="web" text="Web Development" />
                        <SelectItem value="cloud" text="Cloud Computing" />
                        <SelectItem value="data" text="Data Science" />
                      </Select>
                      <Button kind="primary" size="sm">
                        Browse Catalog
                      </Button>
                    </div>
                  </div>

                  <DataTable rows={pathTableRows} headers={pathTableHeaders} isSortable size="md">
                    {({ rows, headers, getHeaderProps, getTableProps }) => (
                      <Table {...getTableProps()}>
                        <TableHead>
                          <TableRow>
                            {headers.map(header => (
                              <TableHeader {...getHeaderProps({ header })}>{header.header}</TableHeader>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map(row => (
                            <TableRow key={row.id}>
                              {row.cells.map(cell => (
                                <TableCell key={cell.id}>{cell.value}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </DataTable>
                </Stack>
              </TabPanel>

              {/* Recent Activity Tab */}
              <TabPanel>
                <Stack gap={5} style={{ marginTop: 'var(--cds-spacing-05)' }}>
                  <Heading size="md">Recent Learning Activity</Heading>

                  <DataTable rows={activityTableRows} headers={activityTableHeaders} isSortable size="md">
                    {({ rows, headers, getHeaderProps, getTableProps }) => (
                      <Table {...getTableProps()}>
                        <TableHead>
                          <TableRow>
                            {headers.map(header => (
                              <TableHeader {...getHeaderProps({ header })}>{header.header}</TableHeader>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map(row => (
                            <TableRow key={row.id}>
                              {row.cells.map(cell => (
                                <TableCell key={cell.id}>{cell.value}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </DataTable>
                </Stack>
              </TabPanel>

              {/* Analytics Tab */}
              <TabPanel>
                <Stack gap={5} style={{ marginTop: 'var(--cds-spacing-05)' }}>
                  <Heading size="md">Learning Analytics</Heading>

                  <InlineNotification
                    kind="info"
                    title="Analytics Coming Soon"
                    subtitle="Detailed learning analytics and performance insights will be available in the next release."
                    hideCloseButton
                  />

                  <Grid condensed>
                    <Column lg={8} md={4} sm={4}>
                      <Tile style={{ padding: 'var(--cds-spacing-06)', textAlign: 'center' }}>
                        <Analytics
                          size={48}
                          style={{ color: 'var(--cds-interactive-01)', marginBottom: 'var(--cds-spacing-04)' }}
                        />
                        <Heading size="md">Performance Trends</Heading>
                        <p style={{ color: 'var(--cds-text-secondary)' }}>
                          Track your learning velocity and skill development over time
                        </p>
                      </Tile>
                    </Column>
                    <Column lg={8} md={4} sm={4}>
                      <Tile style={{ padding: 'var(--cds-spacing-06)', textAlign: 'center' }}>
                        <Growth
                          size={48}
                          style={{ color: 'var(--cds-support-success)', marginBottom: 'var(--cds-spacing-04)' }}
                        />
                        <Heading size="md">Skill Mapping</Heading>
                        <p style={{ color: 'var(--cds-text-secondary)' }}>
                          Visualize your skill progression and identify areas for improvement
                        </p>
                      </Tile>
                    </Column>
                  </Grid>
                </Stack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Column>
      </Grid>
    </Content>
  );
};

export default LearningDashboard;
